import React, { createContext, useContext, useEffect, useRef, useState } from "react"
import { CampingStyleProps, NewReservationProps, ReservationProps, SiteProps } from "../../interfaces"
import { NewReservationAction } from "~/reducers/newReservation"
import { validElectric, validRigType } from "~/types"
import { AdminContext } from "../AdminContext"
import { LONG_TERM_RESERVATION_KINDS, longTermReservationKind, RESERVATION_KINDS, reservationKind } from "~/constants"
import IOrder from "../../interfaces/IOrder"
import IOrderPreview from "../../interfaces/IOrderPreview"
import ILineItem from "../../interfaces/ILineItem"
import api from "../../api2"
import { useDebouncedCallback } from "use-debounce"
import { depositLineItemParams } from "~/api2/orders"
import isServiceFeePaymentMethod from "../../utils/admin/isServiceFeePaymentMethod"
import serviceRateModeForPaymentType from "../../utils/serviceRateModeForPaymentType"
import { bySort } from "~/utils/sorts"
import ePaymentType from "~/enums/ePaymentType"
import { ReservationPriceDetailsProps, UnpaidProps } from "~/api2/reservations"
import { PriceDetails, priceDetailsFor } from "~/utils/priceDetailsFor"
import Api2 from "~/api2"
import { COOKIE_SENDING_MODE_NEW_RESERVATION, ReservationOptions } from "~/constants/NewReservation"
import IGroupedReservation from "~/interfaces/IGroupedReservation"
import { mapLineItemsToPriceDetailParams } from "~/components/Admin/Reservations/NewReservationModal/utils"
import { PaymentMethodType } from "~/interfaces/IPaymentMethods"
import { useCookies } from "react-cookie"
import eSendingMode from "~/enums/eSendingMode"

interface ICtxPayload {
  newReservation: NewReservationProps
  dispatch: any
  inputNotes: string
  setInputNotes: (v: string) => any
  inputFirstName: string
  setInputFirstName: (v: string) => any
  inputLastName: string
  setInputLastName: (v: string) => any
  inputEmail: string
  setInputEmail: (v: string) => any
  inputPhone: string
  setInputPhone: (v: string) => any
  inputPhoneNumber: string
  setInputPhoneNumber: (v: string) => any
  inputPhoneCountryCode: string
  setInputPhoneCountryCode: (v: string) => any
  inputVehicleRigLength: string
  setInputVehicleRigLength: (v: string) => any
  inputVehicleElectric: validElectric
  setInputVehicleElectric: (v: validElectric) => any
  inputVehicleRigType: validRigType
  setInputVehicleRigType: (v: validRigType) => any
  inputVehicleLicense: string
  setInputVehicleLicense: (v: string) => any
  inputVehicleYear: number
  setInputVehicleYear: (v: number | null) => any
  inputVehicleMake: string
  setInputVehicleMake: (v: string) => any
  inputVehicleColor: string
  setInputVehicleColor: (v: string) => any
  inputAdults: number
  setInputAdults: (v: number) => any
  inputChildren: number
  setInputChildren: (v: number) => any
  inputPets: string
  setInputPets: (v: string) => any
  isAcceptingDeposit: boolean
  setIsAcceptingDeposit: (v: boolean) => any
  clearForm: () => any
  selectedReservationType: reservationKind
  setSelectedReservationType: (v: reservationKind) => any
  isPayingSeparately: boolean
  setIsPayingSeparately: (v: boolean) => any
  recurringKind: longTermReservationKind
  setRecurringKind: (v: longTermReservationKind) => any
  selectedSitesForGroup: SiteProps[]
  setSelectedSitesForGroup: React.Dispatch<React.SetStateAction<SiteProps[]>>
  address: string
  setAddress: React.Dispatch<React.SetStateAction<string>>
  selectedCampingStyle: CampingStyleProps
  isLoadingOrderPreview: boolean
  order: IOrder | null
  setOrder: React.Dispatch<React.SetStateAction<IOrder | null>>
  preview: IOrderPreview
  setPreview: React.Dispatch<React.SetStateAction<IOrderPreview>>
  lineItems: Partial<ILineItem>[]
  setLineItems: React.Dispatch<React.SetStateAction<Partial<ILineItem>[]>>
  userLineItems: Partial<ILineItem>[]
  setUserLineItems: React.Dispatch<React.SetStateAction<Partial<ILineItem>[]>>
  depositPreview: IOrderPreview
  createReservation: (data: ReservationOptions) => Promise<boolean | void>
  errorResponse: any
  setErrorResponse: React.Dispatch<React.SetStateAction<any>>
  isShowingCc: boolean
  setIsShowingCc: React.Dispatch<React.SetStateAction<boolean>>
  isProcessing: boolean
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>
  rigLengthError: string | null
  setRigLengthError: React.Dispatch<React.SetStateAction<string | null>>
  isDiscountAdded: boolean
  setIsDiscountAdded: React.Dispatch<React.SetStateAction<boolean>>
  paymentFormRef: React.MutableRefObject<null>
  inputCamperId: string
  setInputCamperId: React.Dispatch<React.SetStateAction<string>>
  selectedPaymentMethodType: PaymentMethodType
  setSelectedPaymentMethodType: React.Dispatch<React.SetStateAction<PaymentMethodType>>
  selectedSendingMode: eSendingMode
  setSelectedSendingMode: (value: eSendingMode, changeCookie?: boolean) => void
}

const initialContext = {
  newReservation: null,
  dispatch: null,
  inputNotes: null,
  setInputNotes: null,
  inputFirstName: null,
  setInputFirstName: null,
  inputLastName: null,
  setInputLastName: null,
  inputEmail: null,
  setInputEmail: null,
  inputPhone: null,
  setInputPhone: null,
  inputPhoneNumber: null,
  setInputPhoneNumber: null,
  inputPhoneCountryCode: null,
  setInputPhoneCountryCode: null,
  inputVehicleRigLength: null,
  setInputVehicleRigLength: null,
  inputVehicleElectric: null,
  setInputVehicleElectric: null,
  inputVehicleRigType: null,
  setInputVehicleRigType: null,
  inputVehicleLicense: null,
  setInputVehicleLicense: null,
  inputVehicleYear: null,
  setInputVehicleYear: null,
  inputVehicleMake: null,
  setInputVehicleMake: null,
  inputVehicleColor: null,
  setInputVehicleColor: null,
  inputAdults: null,
  setInputAdults: null,
  inputChildren: null,
  setInputChildren: null,
  inputPets: null,
  setInputPets: null,
  isAcceptingDeposit: null,
  setIsAcceptingDeposit: null,
  clearForm: null,
  selectedReservationType: null,
  setSelectedReservationType: null,
  isPayingSeparately: null,
  setIsPayingSeparately: null,
  recurringKind: null,
  setRecurringKind: null,
  selectedSitesForGroup: null,
  setSelectedSitesForGroup: null,
  address: null,
  setAddress: null,
  selectedCampingStyle: null,
  isLoadingOrderPreview: false,
  order: null,
  setOrder: null,
  preview: null,
  setPreview: null,
  lineItems: [],
  setLineItems: null,
  userLineItems: [],
  setUserLineItems: null,
  depositPreview: null,
  createReservation: async (data: ReservationOptions) => Promise.resolve(false),
  errorResponse: null,
  setErrorResponse: null,
  isShowingCc: null,
  setIsShowingCc: null,
  isProcessing: null,
  setIsProcessing: null,
  rigLengthError: null,
  setRigLengthError: () => { },
  isDiscountAdded: false,
  setIsDiscountAdded: () => { },
  paymentFormRef: null,
  inputCamperId: null,
  setInputCamperId: () => { },
  selectedPaymentMethodType: null,
  setSelectedPaymentMethodType: (value: PaymentMethodType | null) => { },
  selectedSendingMode: null,
  setSelectedSendingMode: () => {  },
}

export const NewReservationContext = createContext<ICtxPayload>(initialContext)

// export const NewReservationContext = createContext<ICtxPayload>(initialContext)
export const NewReservationProvider = ({
  newReservation,
  dispatch,
  children,
}: {
  newReservation: NewReservationProps
  dispatch: any
  children: any
}) => {
  const { showRefreshModal, updateLocalReservations, camp, sites, campingStyles } =
    useContext(AdminContext)

  const site = sites?.find((s) => s.id === newReservation.site_id)
  const campingStyle = site ? campingStyles.find((cs) => cs.id === site.camping_style_id) : undefined

  const [isLoadingOrderPreview, setIsLoadingOrderPreview] = useState(false)
  const [order, setOrder] = useState<IOrder | null>()
  const [preview, setPreview] = useState<IOrderPreview>()
  const [userLineItems, setUserLineItems] = useState<Partial<ILineItem>[]>([])
  const [systemLineItems, setSystemLineItems] = useState<Partial<ILineItem>[]>([])
  const [depositOrder, setDepositOrder] = useState<IOrder>()

  const [inputNotes, setInputNotes] = useState(newReservation?.notes)
  const [inputCamperId, setInputCamperId] = useState<string>()
  const [inputFirstName, setInputFirstName] = useState<string>()
  const [inputLastName, setInputLastName] = useState<string>()
  const [inputEmail, setInputEmail] = useState<string>()
  const [inputPhone, setInputPhone] = useState<string>()
  const [inputPhoneNumber, setInputPhoneNumber] = useState<string>()
  const [inputPhoneCountryCode, setInputPhoneCountryCode] = useState<string>()
  const [inputVehicleRigLength, setInputVehicleRigLength] = useState<string>()
  const [inputVehicleElectric, setInputVehicleElectric] = useState<validElectric>()
  const [inputVehicleRigType, setInputVehicleRigType] = useState<validRigType>()
  const [inputVehicleLicense, setInputVehicleLicense] = useState<string>()
  const [inputVehicleYear, setInputVehicleYear] = useState<number>()
  const [inputVehicleMake, setInputVehicleMake] = useState<string>()
  const [inputVehicleColor, setInputVehicleColor] = useState<string>()
  const [inputAdults, setInputAdults] = useState<number>(newReservation?.passengers)
  const [inputChildren, setInputChildren] = useState<number>(newReservation?.kid_campers)
  const [inputPets, setInputPets] = useState<string>()
  const [isAcceptingDeposit, setIsAcceptingDeposit] = useState<boolean>(false)
  const [address, setAddress] = useState<string>()
  const [selectedReservationType, setSelectedReservationType] = useState<reservationKind>(RESERVATION_KINDS.SINGLE)
  const [isPayingSeparately, setIsPayingSeparately] = useState(true)
  const [recurringKind, setRecurringKind] = useState<longTermReservationKind>(
    LONG_TERM_RESERVATION_KINDS.RENT_AND_UTILITIES,
  )
  const [selectedSitesForGroup, setSelectedSitesForGroup] = useState<SiteProps[]>([site].filter((s) => s))

  const paymentFormRef = useRef(null)
  const [isShowingCc, setIsShowingCc] = useState(false)
  const [errorResponse, setErrorResponse] = useState<any>()
  const [rigLengthError, setRigLengthError] = useState<string | null>(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [isDiscountAdded, setIsDiscountAdded] = useState<boolean>(false)
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<PaymentMethodType>()
  const hasModifiedPaymentMethod = useRef(false)

  const [cookies, setCookie] = useCookies([COOKIE_SENDING_MODE_NEW_RESERVATION])

  const isValidSendingMode = Object.values(eSendingMode).includes(cookies[COOKIE_SENDING_MODE_NEW_RESERVATION])
  const [selectedSendingMode, setSelectedSendingMode] = useState<eSendingMode>(isValidSendingMode ? cookies[COOKIE_SENDING_MODE_NEW_RESERVATION] : eSendingMode.NONE)

  const handleChangeSendingMode = (value: eSendingMode, changeCookie: boolean = true) => {
    if (changeCookie) {
      setCookie(COOKIE_SENDING_MODE_NEW_RESERVATION, value)
    }
    setSelectedSendingMode(value)
  }

  useEffect(() => {
    updateSelectedSitesForGroup()
  }, [newReservation])

  useEffect(() => {
    setUserLineItems([])
  }, [isPayingSeparately, newReservation.kind === RESERVATION_KINDS.GROUP])

  const generateOrder = useDebouncedCallback(() => {
    const missingFields = [
      !newReservation.site_id && "site",
      !newReservation.start_date && "start date",
      !newReservation.end_date && "end date",
    ].filter(Boolean)

    if (missingFields.length) {
      onCreateError(`Missing ${missingFields.join(", ")}`)
      setIsLoadingOrderPreview(false)
      return
    }

    let data: Parameters<typeof api.PriceDetails.orderForSites>[1] = {
      subtotalOverride: newReservation.edited_total ? newReservation.subtotal : null,
      kind:
        selectedReservationType === RESERVATION_KINDS.RECURRING &&
          (recurringKind === LONG_TERM_RESERVATION_KINDS.UTILITIES_ONLY ||
            recurringKind === LONG_TERM_RESERVATION_KINDS.SEASONAL_ONLY)
          ? RESERVATION_KINDS.SEASONAL
          : (newReservation.kind ?? RESERVATION_KINDS.SINGLE),
      campingStyleId: sites?.find((s) => s.id === newReservation.site_id)?.camping_style_id,
      startDate: newReservation.start_date?.format(),
      endDate: newReservation.end_date?.format(),
      discountSlug: newReservation.discount_name,
      isTaxEnabled: newReservation.is_tax_enabled,
      couponCodes: [],
      is_passenger_fees_enabled:
        newReservation.kind === RESERVATION_KINDS.GROUP ? false : newReservation.is_passenger_fees_enabled, // TODO: toggle isPassengerFeesEnabled && props.isShowingPassengerFeeToggle,
      interval_id: newReservation.interval_id,
      user_electric: inputVehicleElectric as validElectric,
      number_of_adults: inputAdults,
      number_of_kids: inputChildren,
      number_of_pets: Number(campingStyle?.extra_pets_price_in_cents) > 0 ? Number(inputPets) : undefined,
      isHolidayEnabled: newReservation.is_holiday_rate_enabled,
      isWeekendEnabled: newReservation.is_weekend_fee_enabled,
      isCreditCardFeeEnabled: newReservation.is_credit_card_fee_enabled,
      isServiceFeeApplied: isServiceFeePaymentMethod(newReservation.paymentMethod),
      line_items_attributes: mapLineItemsToPriceDetailParams(userLineItems || [], newReservation.is_tax_enabled),
      isPetsFeeEnabled: newReservation.kind === RESERVATION_KINDS.GROUP ? false : newReservation.is_pets_fee_enabled,
      serviceRateMode: serviceRateModeForPaymentType(newReservation.paymentMethod),
      selected_payment_method: selectedPaymentMethodType
    }
    if (data.startDate && (!data.endDate || newReservation.site_id.length === 0)) {
      setIsLoadingOrderPreview(false)
      return
    }
    const siteIds =
      newReservation.kind === RESERVATION_KINDS.GROUP
        ? { site_ids: selectedSitesForGroup.map((s) => s.id) }
        : site?.id
          ? { site_id: site.id }
          : undefined
    api.PriceDetails.orderForSites(siteIds, data)
      .then(({ orders, order: servOrder }) => {
        const respOrder = orders ? consolidateOrdersForGroup(orders) : servOrder

        const newLineItems = respOrder?.line_items || []

        if (JSON.stringify(respOrder) !== JSON.stringify(order)) {
          setSystemLineItems(newLineItems)
        }

        setOrder(respOrder)
        setIsLoadingOrderPreview(false)
      })
      .catch((err) => {
        console.error(err)
        setIsLoadingOrderPreview(false)
      })
      .finally(() => setIsLoadingOrderPreview(false))
  }, 500)

  const consolidateOrdersForGroup = (orders: IOrder[]) =>
    orders.reduce((acc, order): IOrder => {
      return {
        ...acc,
        subtotal_in_cents: (acc.subtotal_in_cents || 0) + order.subtotal_in_cents,
        discount_in_cents: (acc.discount_in_cents || 0) + order.discount_in_cents,
        discount_name: acc.discount_name || order.discount_name,
        discounted_subtotal_in_cents: (acc.discounted_subtotal_in_cents || 0) + order.discounted_subtotal_in_cents,
        service_fee_in_cents: (acc.service_fee_in_cents || 0) + order.service_fee_in_cents,
        service_rate: acc.service_rate || 0, // TODO: probably need the average
        credit_card_fee_in_cents: (acc.credit_card_fee_in_cents || 0) + order.credit_card_fee_in_cents,
        credit_card_rate: acc.credit_card_rate || 0, // TODO: probably need the average
        tax_fee_in_cents: (acc.tax_fee_in_cents || 0) + order.tax_fee_in_cents,
        tax_rate: acc.tax_rate || 0, // TODO: probably need the average
        unpaid_subtotal_in_cents: (acc.unpaid_subtotal_in_cents || 0) + order.unpaid_subtotal_in_cents,
        unpaid_tax_fee_in_cents: (acc.unpaid_tax_fee_in_cents || 0) + order.unpaid_tax_fee_in_cents,
        paid_service_fee_in_cents: (acc.paid_service_fee_in_cents || 0) + order.paid_service_fee_in_cents,
        total_in_cents: (acc.total_in_cents || 0) + order.total_in_cents,
        paid_total_in_cents: (acc.paid_total_in_cents || 0) + order.paid_total_in_cents,
        unpaid_total_in_cents: (acc.unpaid_total_in_cents || 0) + order.unpaid_total_in_cents,
        line_items: (acc.line_items || []).reduce((acc, item) => {
          const existingReservationItem = acc.find((i) => i.reservation_id)
          if (existingReservationItem && item.reservation_id) {
            existingReservationItem.total_amount_in_cents += item.total_amount_in_cents
            existingReservationItem.quantity += item.quantity
            existingReservationItem.unit_amount_in_cents = existingReservationItem.total_amount_in_cents / existingReservationItem.quantity / (existingReservationItem.occurrences || 1)
          } else {
            acc.push(item)
          }
          return acc
        }, order.line_items),
      }
    }, {} as IOrder)

  useEffect(() => {
    setIsLoadingOrderPreview(true)
    generateOrder()
    if (Number(newReservation.deposit_in_cents) > 0 && isAcceptingDeposit) {
      buildDepositOrder()
      if (!hasModifiedPaymentMethod.current && newReservation.paymentMethod === ePaymentType.CASH_UNPAID) {
        newReservation.paymentMethod = ePaymentType.CASH_PAID
        hasModifiedPaymentMethod.current = true
      }
    } else {
      hasModifiedPaymentMethod.current = false
      setDepositOrder(null)
    }
  }, [
    selectedSitesForGroup?.join(""),
    newReservation.edited_total,
    newReservation.subtotal,
    newReservation.site_id,
    newReservation.start_date,
    newReservation.end_date,
    newReservation.start_time,
    newReservation.end_time,
    newReservation.interval_id,
    newReservation.paymentMethod,
    inputVehicleElectric,
    recurringKind,
    newReservation.discount_name,
    newReservation.is_holiday_rate_enabled,
    newReservation.is_weekend_fee_enabled,
    newReservation.is_tax_enabled,
    newReservation.is_passenger_fees_enabled,
    newReservation.is_pets_fee_enabled,
    newReservation.is_credit_card_fee_enabled,
    newReservation.kind,
    inputAdults,
    inputChildren,
    inputPets,
    userLineItems?.reduce((acc, li) => acc + (li.total_amount_in_cents || 0), 0),
    userLineItems?.map((li) => li.label).join(""),
    isAcceptingDeposit,
    newReservation.deposit_in_cents,
    selectedPaymentMethodType
  ])

  const buildDepositOrder = useDebouncedCallback(async () => {
    if (!camp || !campingStyle) return

    api.Orders.createPreview({
      order_id: null, // TODO: might not work
      camp_id: camp.id,
      camping_style_id: campingStyle.id,
      is_credit_card_fee_enabled: newReservation.is_credit_card_fee_enabled,
      is_service_fee_enabled: isServiceFeePaymentMethod(newReservation.paymentMethod),
      service_rate_mode: serviceRateModeForPaymentType(newReservation.paymentMethod),
      discount_slug: null,
      line_items_attributes: depositLineItemParams(newReservation.deposit_in_cents * 100.0, false),
    })
      .then(({ order: { preview } }) => {
        setDepositOrder(preview)
      })
      .catch((err) => {
        console.error(err)
      })
  }, 500)

  const updateSelectedSitesForGroup = () => {
    if (selectedSitesForGroup.length > 0) return
    setSelectedSitesForGroup([site].filter((s) => s))
  }

  const setIntervalId = () => {
    if (!campingStyle?.booking_intervals?.length) {
      dispatch({
        type: NewReservationAction.UPDATE_INTERVAL_ID,
        payload: {
          interval_id: null,
        },
      })
      return
    }

    // Don't override if an interval is already set (let the component handle availability checking)
    if (newReservation.interval_id) {
      return
    }

    const interval = campingStyle.booking_intervals.sort(bySort)[0]
    
    dispatch({
      type: NewReservationAction.UPDATE_INTERVAL_ID,
      payload: {
        interval_id: interval.id,
      },
    })
  }

  const clearForm = () => {
    setInputNotes(null)
    setInputFirstName(null)
    setInputLastName(null)
    setInputEmail(null)
    setInputPhone(null)
    setInputPhoneNumber(null)
    setInputPhoneCountryCode(null)
    setInputVehicleRigLength(null)
    setInputVehicleElectric(null)
    setInputVehicleRigType(null)
    setInputVehicleLicense(null)
    setInputVehicleYear(null)
    setInputVehicleMake(null)
    setAddress(null)
    setInputVehicleColor(null)
    setInputAdults(null)
    setInputChildren(null)
    setInputPets(null)
    setIsAcceptingDeposit(null)
    setSelectedReservationType(RESERVATION_KINDS.SINGLE)
    setRecurringKind(LONG_TERM_RESERVATION_KINDS.RENT_AND_UTILITIES)
    setErrorResponse(null)
    setSelectedSitesForGroup([])
    setOrder(null)
    setUserLineItems(null)
    setSystemLineItems(null)
    setInputCamperId(null)
    dispatch({
      type: NewReservationAction.CLEAR_FORM,
    })
  }

  useEffect(() => {
    if (!isAcceptingDeposit || newReservation.deposit_in_cents || !order) return

    const defaultDeposit = campingStyle ? campingStyle.default_deposit_in_cents : 0
    dispatch({
      type: NewReservationAction.UPDATE_DEPOSIT_AMOUNT,
      payload: {
        deposit_in_cents:
          isAcceptingDeposit && order && defaultDeposit
            ? defaultDeposit > order.total_in_cents
              ? (order.total_in_cents - 1) / 100
              : defaultDeposit / 100
            : 0,
      },
    })
  }, [isAcceptingDeposit, newReservation.total_owed_in_cents, order])

  useEffect(() => {
    if (errorResponse) {
      setErrorResponse(null)
    }
  }, [
    inputNotes,
    inputFirstName,
    inputLastName,
    inputEmail,
    inputPhone,
    inputPhoneNumber,
    inputPhoneCountryCode,
    inputVehicleRigLength,
    inputVehicleElectric,
    inputVehicleRigType,
    inputVehicleLicense,
    inputVehicleYear,
    inputVehicleMake,
    inputVehicleColor,
    inputAdults,
    inputChildren,
    inputPets,
    isAcceptingDeposit,
    newReservation,
    address,
  ])

  const onCreateError = (error: any) => {
    if (!error.status) {
      setErrorResponse(error)
      setIsProcessing(false)
    } else {
      if (error?.status === 422) {
        setErrorResponse(error)
        setIsProcessing(false)
      } else {
        showRefreshModal()
        setErrorResponse(error)
        setIsProcessing(false)
        clearForm()
      }
    }
  }

  const handleCreatedResponse = async (resp: {
    reservation?: ReservationProps
    reservations?: ReservationProps[]
    grouped_reservation?: IGroupedReservation
  }) => {
    const isGroupReservation = !!resp.reservations

    if (!resp.reservation && !isGroupReservation) {
      onCreateError({ message: "Something went wrong. Please try again." })
      return
    }

    const order: IOrder | undefined = resp.grouped_reservation
      ? ({
        id: resp.grouped_reservation.order_id,
        reservation_id: resp.grouped_reservation.id,
        camper_id: resp.grouped_reservation?.camper_id
      } as IOrder)
      : resp.reservation?.order

    try {

      await (paymentFormRef as any).current?.submitPayment(
        undefined,
        order,
        isGroupReservation,
        newReservation.is_tax_enabled,
        newReservation.deposit_in_cents > 0,
      )
    } catch (e) {
      if (isGroupReservation && resp.grouped_reservation)
        await Api2.GroupedReservations.destroy(resp.grouped_reservation.id)
      else if (resp.reservation)
        await Api2.Reservations.remove(resp.reservation.id!).catch(() =>
          console.error("failed to remove reservation no id present"),
        )
      throw {
        status: 422,
        message:
          e instanceof Error
            ? e.message
            : (e as any).error?.message
              ? (e as any).error?.message
              : "Payment submission failed",
        details: e instanceof Error ? e.message : String(e),
        code: "PAYMENT_SUBMISSION_ERROR",
      } as const
    }

    setErrorResponse(null)
    setIsProcessing(true)

    if (isGroupReservation && resp.reservations) {
      updateLocalReservations(resp.reservations)
    } else if (resp.reservation) {
      dispatch({
        type: NewReservationAction.UPDATE_ID,
        payload: { id: resp.reservation.id },
      })
      updateLocalReservations([resp.reservation])
    }

    clearForm()
    setIsProcessing(false)
  }

  const handleRecurringCreatedResponse = async (resp: { reservations: ReservationProps[] }, isPayingNow: boolean) => {
    setIsProcessing(true)

    const order: IOrder | undefined = resp?.reservations?.[0]?.order

    if ((paymentFormRef as any).current && order) {
      await (paymentFormRef as any).current?.submitPayment(undefined, order, false, newReservation.is_tax_enabled)
    }

    setErrorResponse(null)

    updateLocalReservations(resp.reservations)

    clearForm()

    if (isPayingNow) {
    }

    setIsProcessing(false)
  }

  const createReservation = async (options: ReservationOptions): Promise<boolean> => {
    const missingFields = [
      !newReservation.site_id && "site",
      !newReservation.start_date && "start date",
      !newReservation.end_date && "end date",
    ].filter(Boolean)

    if (missingFields.length) {
      onCreateError(`Missing ${missingFields.join(", ")}`)
      return false
    }

    const {
      isPrePaid = false,
      withLink = false,
      isPayingNow = false,
      isGroupSeparated = false,
      recurringKind = null,
    } = options
    setIsProcessing(true)

    if (!newReservation.start_date) {
      onCreateError({
        message: "Validation failed: Please select a start date",
      })
      return false
    }

    if (!newReservation.end_date) {
      onCreateError({ message: "Validation failed: Please select an end date" })
      return false
    }

    let siteId = newReservation.site_id
    const startDate = newReservation.start_date?.format()
    const endDate = newReservation.end_date?.format()
    const notes = inputNotes
    const userParams = {
      first_name: inputFirstName as string,
      last_name: inputLastName as string,
      email: inputEmail as string,
      phone: inputPhone as string,
      phone_number: inputPhoneNumber as string,
      phone_country_code: inputPhoneCountryCode as string,
      vehicle_rig_length: inputVehicleRigLength,
      vehicle_electric: inputVehicleElectric,
      vehicle_rig_type: inputVehicleRigType,
      license_plate: inputVehicleLicense,
      vehicle_year: inputVehicleYear,
      vehicle_make: inputVehicleMake,
      vehicle_color: inputVehicleColor,
      adults: inputAdults,
      children: inputChildren,
      pets_info: inputPets,
      address: address,
    }
    const isGroup = newReservation.kind === RESERVATION_KINDS.GROUP
    const discountName = newReservation.discount_name
    const passengers = isGroup ? 0 : inputAdults
    const kids = isGroup ? 0 : inputChildren

    const bookingIntervals = campingStyle?.booking_intervals
    const hasBookingIntervals = Number(bookingIntervals?.length) > 0
    const defaultHours = Number(campingStyle?.default_hours)
    const isHourly = hasBookingIntervals || defaultHours > 0

    let subtotalOverride = null

    if (newReservation.edited_total) subtotalOverride = newReservation.subtotal

    const priceDetailsParams: ReservationPriceDetailsProps = {
      kind: newReservation.kind || RESERVATION_KINDS.SINGLE,
      start_date: startDate,
      end_date: endDate,
      discount_slug: discountName,
      subtotal_override: subtotalOverride,
      is_tax_enabled: newReservation.is_tax_enabled,
      is_holiday_rate_enabled: newReservation.is_holiday_rate_enabled,
      is_weekend_fee_enabled: newReservation.is_weekend_fee_enabled,
      is_passenger_fees_enabled: isGroup ? false : newReservation.is_passenger_fees_enabled,
      is_pets_fee_enabled: newReservation.is_pets_fee_enabled,
      number_of_adults: passengers,
      number_of_kids: kids,
      number_of_pets: Number(campingStyle?.extra_pets_price_in_cents) > 0 ? Number(inputPets) : undefined,
      user_electric: inputVehicleElectric,
      is_credit_card_fee_enabled: newReservation.is_credit_card_fee_enabled,
      interval_id: newReservation.interval_id,
      selected_payment_method: selectedPaymentMethodType
    }

    const isGrouped = selectedReservationType === RESERVATION_KINDS.GROUP
    const isBlocked = selectedReservationType === RESERVATION_KINDS.BLOCKED
    let isMultipleSites = isGrouped || isBlocked
    let siteIds = null // NOTE: only not null if isMultipleSites

    if (isMultipleSites && selectedSitesForGroup.length > 0) {
      siteIds = selectedSitesForGroup.filter((site) => site).map((site) => site.id)
    }

    const site =
      siteIds && siteIds?.length > 0 ? sites?.find((s) => s.id === siteIds[0]) : sites?.find((s) => s.id === siteId)
    siteId = site?.id

    // TODO: refactor this to simplify the params and DRY it up (use above params)
    //  Goncalo - I AGREE, but I wont refactor it now
    const { price_detail: priceDetails } = (await priceDetailsFor({
      kind:
        selectedReservationType === RESERVATION_KINDS.RECURRING &&
          recurringKind === LONG_TERM_RESERVATION_KINDS.UTILITIES_ONLY
          ? RESERVATION_KINDS.SEASONAL
          : newReservation.kind || RESERVATION_KINDS.SINGLE,
      camp,
      site: site,
      startDate: startDate,
      endDate: endDate,
      discountSlug: newReservation.discount_name,
      subtotalOverride: newReservation.edited_total ? newReservation.subtotal : null,
      isTaxableEnabled: newReservation.is_tax_enabled,
      isPassengerFeesEnabled: newReservation.is_passenger_fees_enabled,
      intervalId: newReservation.interval_id,
      userElectric: inputVehicleElectric,
      numberOfAdults: inputAdults,
      numberOfKids: inputChildren,
      numberOfPets: Number(campingStyle?.extra_pets_price_in_cents) > 0 ? Number(inputPets) : undefined,
      isHolidayEnabled: newReservation.is_holiday_rate_enabled,
      isCreditCardFeeEnabled: newReservation.is_credit_card_fee_enabled,
      isWeekendEnabled: newReservation.is_weekend_fee_enabled,
      selectedPaymentMethod: selectedPaymentMethodType,
    })) as { order: IOrder; price_detail: PriceDetails }

    const total = priceDetails.total
    const data: UnpaidProps = {
      end_time: isHourly ? newReservation.end_time || "" : "",
      site_ids: null,
      start_time: isHourly ? newReservation.start_time || "" : "",
      start_date: startDate,
      end_date: endDate,
      interval_id: newReservation.interval_id,
      notes: notes,
      discount_name: discountName,
      passengers,
      kid_campers: kids,
      vehicle_license_plate: userParams.license_plate,
      sending_mode: selectedSendingMode,
      unpaid_price_in_cents: (newReservation.total_owed_in_cents || 0) * 100,
      deposit_in_cents: isAcceptingDeposit ? newReservation.deposit_in_cents * 100 : null,
      ...userParams,
      price_details_params: priceDetailsParams,
      line_items_attributes: mapLineItemsToPriceDetailParams(systemLineItems, newReservation.is_tax_enabled),
      admin_email: isShowingCc ? camp.email : undefined,
    }
    const paymentMethod = (paymentFormRef as any)?.current?.paymentType

    try {
      if (selectedReservationType === RESERVATION_KINDS.BLOCKED) {
        const resp = await Api2.Reservations.createBlocked(siteId, {
          start_date: startDate,
          end_date: endDate,
          interval_id: newReservation.interval_id,
          notes: notes,
          site_ids: siteIds,
        })
        await handleCreatedResponse(resp)
      } else if (isPrePaid && selectedReservationType !== RESERVATION_KINDS.RECURRING) {
        const resp = await Api2.Reservations.createPaid(siteId, {
          ...data,
          is_credit_card_payment: [ePaymentType.CARD_INPUT, ePaymentType.CARD_READER].includes(paymentMethod),
          unpaid_price_in_cents: order ? order.unpaid_total_in_cents : total * 100,
          is_grouped: isGrouped,
          site_ids: siteIds,
        })
        await handleCreatedResponse(resp)
      } else if (selectedReservationType === RESERVATION_KINDS.RECURRING) {
        const resp = await Api2.Reservations.createRecurring(siteId, {
          ...data,
          is_credit_card_payment: [ePaymentType.CARD_INPUT, ePaymentType.CARD_READER].includes(paymentMethod),
          recurring_price_in_cents: total * 100,
          with_payment_link: withLink,
          mark_as_paid: isPrePaid && !data.deposit_in_cents,
          kind: recurringKind as longTermReservationKind,
          ...userParams,
        })
        await handleRecurringCreatedResponse(resp, isPayingNow)
      } else {
        const resp = await Api2.Reservations.createUnpaid(siteId, {
          ...data,
          is_credit_card_payment: [ePaymentType.CARD_INPUT, ePaymentType.CARD_READER].includes(paymentMethod),
          unpaid_price_in_cents: order ? order.unpaid_total_in_cents : total * 100,
          with_payment_link: withLink,
          is_grouped: isGrouped,
          site_ids: siteIds,
          is_group_separated: isGroupSeparated,
          is_manual_charge: true
        })
        await handleCreatedResponse(resp)
      }
      return true
    } catch (error) {
      onCreateError(error)
      return false
    } finally {
      setIsProcessing(false)
    }
  }

  return (
    <NewReservationContext.Provider
      value={{
        newReservation: newReservation,
        dispatch: dispatch,
        inputNotes: inputNotes,
        setInputNotes: setInputNotes,
        inputFirstName: inputFirstName,
        setInputFirstName: setInputFirstName,
        inputLastName: inputLastName,
        setInputLastName: setInputLastName,
        inputEmail: inputEmail,
        setInputEmail: setInputEmail,
        inputPhone: inputPhone,
        setInputPhone: setInputPhone,
        inputPhoneNumber: inputPhoneNumber,
        setInputPhoneNumber: setInputPhoneNumber,
        inputPhoneCountryCode: inputPhoneCountryCode,
        setInputPhoneCountryCode: setInputPhoneCountryCode,
        inputVehicleRigLength: inputVehicleRigLength,
        setInputVehicleRigLength: setInputVehicleRigLength,
        inputVehicleElectric: inputVehicleElectric as validElectric,
        setInputVehicleElectric: setInputVehicleElectric,
        inputVehicleRigType: inputVehicleRigType,
        setInputVehicleRigType: setInputVehicleRigType,
        inputVehicleLicense: inputVehicleLicense,
        setInputVehicleLicense: setInputVehicleLicense,
        inputVehicleYear: inputVehicleYear,
        setInputVehicleYear: setInputVehicleYear,
        inputVehicleMake: inputVehicleMake,
        setInputVehicleMake: setInputVehicleMake,
        inputVehicleColor: inputVehicleColor,
        setInputVehicleColor: setInputVehicleColor,
        inputAdults: inputAdults,
        setInputAdults: setInputAdults,
        inputChildren: inputChildren,
        setInputChildren: setInputChildren,
        inputPets: inputPets,
        setInputPets: setInputPets,
        isAcceptingDeposit: isAcceptingDeposit,
        setIsAcceptingDeposit: setIsAcceptingDeposit,
        selectedReservationType: selectedReservationType,
        setSelectedReservationType: setSelectedReservationType,
        isPayingSeparately: isPayingSeparately,
        setIsPayingSeparately: setIsPayingSeparately,
        recurringKind: recurringKind,
        setRecurringKind: setRecurringKind,
        clearForm: clearForm,
        selectedSitesForGroup: selectedSitesForGroup,
        setSelectedSitesForGroup: setSelectedSitesForGroup,
        address: address,
        setAddress: setAddress,
        selectedCampingStyle: campingStyle,
        isLoadingOrderPreview,
        order,
        setOrder,
        preview,
        setPreview,
        lineItems: systemLineItems,
        userLineItems: userLineItems,
        setLineItems: setSystemLineItems,
        setUserLineItems,
        depositPreview: depositOrder as IOrderPreview,
        createReservation,
        errorResponse,
        setErrorResponse,
        isShowingCc,
        setIsShowingCc,
        isProcessing,
        setIsProcessing,
        rigLengthError,
        setRigLengthError,
        isDiscountAdded,
        setIsDiscountAdded,
        paymentFormRef,
        inputCamperId,
        setInputCamperId,
        selectedPaymentMethodType,
        setSelectedPaymentMethodType,
        selectedSendingMode,
        setSelectedSendingMode: handleChangeSendingMode
      }}
    >
      {children}
    </NewReservationContext.Provider>
  )
}
